import type { SessionType, Week } from '../../services/coursesApi';

// Shared presentation helpers for the 14-week calendar (Dashboard cards + LeftNav).

// Count completed vs total lessons across a week's modules.
export function weekLessonProgress(week: Week): { completed: number; total: number } {
  let completed = 0;
  let total = 0;
  for (const m of week.modules ?? []) {
    for (const l of m.lessons ?? []) {
      total += 1;
      if (l.status === 'completed') completed += 1;
    }
  }
  return { completed, total };
}

// First lesson of a week (where a card/folder links). Undefined for non-lecture
// weeks (review/test/reading) that carry no lessons.
export function firstLessonId(week: Week): string | undefined {
  for (const m of week.modules ?? []) {
    if (m.lessons?.length) return m.lessons[0].id;
  }
  return undefined;
}

// A tasteful themed gradient for weeks without a banner image. Hue varies by the
// week index so adjacent cards are distinguishable. Uses oklch so it reads well
// across paper/light/dark (the chrome around it is ws- token driven).
export function weekGradient(index: number): string {
  const hue = (index * 47) % 360; // 47 is coprime-ish with 360 → good spread
  const h2 = (hue + 38) % 360;
  return `linear-gradient(135deg, oklch(72% 0.13 ${hue}) 0%, oklch(64% 0.15 ${h2}) 100%)`;
}

// Chip color classes (ws- tokens) per session type.
export function sessionChipClass(type: SessionType): string {
  switch (type) {
    case 'lecture':
      return 'bg-ws-accent-quiet text-ws-accent-ink';
    case 'review':
    case 'test':
      return 'bg-ws-surface-2 text-ws-warning';
    case 'reading':
      return 'bg-ws-surface-2 text-ws-ink-3';
    case 'intro':
    case 'activity':
      return 'bg-ws-ai-pale text-ws-ai-deep';
    default:
      return 'bg-ws-surface-2 text-ws-ink-2';
  }
}

// Short chip label for a session (compact tiles). Prefer a Lesson N·part tag for
// lectures, else a terse version of the type.
export function sessionShortLabel(s: { type: SessionType; lesson?: number; part?: string; label: string }): string {
  if (s.type === 'lecture' && s.lesson != null) {
    return `L${s.lesson}${s.part ? s.part : ''}`;
  }
  switch (s.type) {
    case 'review':
      return 'Review';
    case 'test':
      return 'Test';
    case 'reading':
      return 'Reading';
    case 'intro':
      return 'Intro';
    case 'activity':
      return 'Activity';
    default:
      return s.label.slice(0, 16);
  }
}

// Does this week have any teachable lesson content (vs. a pure review/test/reading
// week)? Drives whether a card is a link or a static plan card.
export function weekHasLessons(week: Week): boolean {
  return (week.modules ?? []).some((m) => (m.lessons?.length ?? 0) > 0);
}

// Total lesson count across a week's modules.
export function weekLessonCount(week: Week): number {
  return (week.modules ?? []).reduce((n, m) => n + (m.lessons?.length ?? 0), 0);
}

// Rough estimated study time for a week, in minutes. The course tree carries no
// per-lesson duration, so we estimate from lesson count (~15 min each) — enough
// to give the card a useful "time to complete" affordance.
const MINUTES_PER_LESSON = 15;
export function weekEstimatedMinutes(week: Week): number {
  return weekLessonCount(week) * MINUTES_PER_LESSON;
}

// Human label for an estimated-minutes value ("45 min", "1h 30m").
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return '';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// CTA label for a lecture week based on its progress.
export function weekCtaLabel(completed: number, total: number): string {
  if (total === 0) return 'View week';
  if (completed === 0) return 'Start week';
  if (completed >= total) return 'Review week';
  return 'Continue';
}

/**
 * The banner image for a week.
 *
 * `week.image_url` (set per week in the database) wins when present. Otherwise
 * fall back to the authored course artwork in `public/course-weeks/`, which
 * covers weeks 1-14 — the whole course — so in practice every week has a real
 * image and the gradient below is only reached if that file is ever missing.
 *
 * Served from `public/` rather than imported so the files are not bundled:
 * they are 3.4 MB of photography that would otherwise land in the JS chunk,
 * and the browser can cache them independently of a deploy.
 *
 * The source folder held four copies of each week (a short and a long name,
 * each as .jpg and .png) — 56 files, 29 MB. The short/long pairs were
 * byte-identical and the PNGs were 4x the size of the JPEGs for the same
 * 1376x768 pixels, so one JPEG per week is kept.
 */
export function weekImageUrl(week: { index: number; image_url?: string | null }): string | null {
  if (week.image_url) return week.image_url;
  if (week.index >= 1 && week.index <= 14) {
    return `/course-weeks/WEEK-${String(week.index).padStart(2, '0')}.jpg`;
  }
  return null;
}
